import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  computed,
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EMPTY, Subscription, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ExecPlan, Workflow } from '@app/shared/interfaces';
import { WorkflowService } from '../../shared/services/workflow/workflow.service';
import { WorkflowCreateDialogData } from './workflow-create-dialog/workflow-create-dialog.component';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { MessageService } from '../../shared/services/message/message.service';
import { AppEventBus } from '../../shared/services/events/app-events';
import { ProjectService } from '../../shared/services/project/project.service';
import { ExecutionPlanService } from '../../shared/services/execution-plan/execution-plan.service';
import { PermissionId } from '../../shared/constants/permissions.constants';
import { RELOAD_NOTEBOOKS } from '../../shared/constants/general.constants';
import { DateFormattingHelper } from '../../shared/helpers/date-formatting.helper';
import { ToastrService } from 'ngx-toastr';

export interface WorkflowSelectEvent {
  workflow: Workflow;
  workflowName: string;
}

@Component({
  selector: 'app-workflow-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(keydown.escape)': 'onEscapeKey()',
  },
  templateUrl: './workflow-dropdown.component.html',
  styleUrl: './workflow-dropdown.component.scss',
  imports: [MatIconModule, MatButtonModule, MatDialogModule, MatTooltipModule],
})
export class WorkflowDropdownComponent implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);

  // Signal inputs
  projectId = input<string | undefined>(undefined);
  selectedWorkflowId = input<number | undefined>(undefined);
  currentWorkflow = input<string>('New Workflow');
  disabled = input<boolean>(false);

  // Signal outputs
  workflowSelected = output<WorkflowSelectEvent>();
  newWorkflowRequested = output<void>();
  workflowDeleted = output<number>();

  // Internal state
  workflows = signal<Workflow[]>([]);
  isOpen = signal<boolean>(false);
  private subscriptions = new Subscription();
  private notebookService = inject(WorkflowService);
  private dialogService = inject(DialogService);
  private appStateService = inject(AppStateService);
  private messageService = inject(MessageService);
  private appEventBus = inject(AppEventBus);
  private projectService = inject(ProjectService);
  private executionPlanService = inject(ExecutionPlanService);
  private toastr = inject(ToastrService);

  // Computed signals
  isProjectOwner = computed(() => this.appStateService.isProjectOwner());
  isHeadVersion = computed(() => this.appStateService.isHeadVersion());
  isCurrentEditor = computed(() => this.appStateService.isCurrentEditor());
  activeWorkflow = computed(() => this.appStateService.workflow());
  activeWorkflowId = computed(() => this.appStateService.workflowId());

  /** True when the current user can delete workflows (Admin/Owner + HEAD + holds lock). */
  canDeleteWorkflow = computed(
    () =>
      this.isHeadVersion() &&
      this.isCurrentEditor() &&
      (this.isProjectOwner() ||
        this.projectService.getCurrentUserHasProjectPermission(
          PermissionId.ADMIN,
        )),
  );

  /**
   * True when the current user can create workflows.
   *
   * SCRUM-542: previously the "New Workflow" button was gated on
   * `isProjectOwner() && isHeadVersion()`, which strictly requires the project
   * creator (`user.userId === project.userId`). Admins holding the lock were
   * silently blocked even though they can delete workflows. Aliased to
   * `canDeleteWorkflow` so the create and delete gates cannot drift apart.
   */
  canCreateWorkflow = this.canDeleteWorkflow;

  ngOnInit(): void {
    this.loadWorkflows();
    this.setupMessageSubscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupMessageSubscription(): void {
    const messageSub = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message?.text === RELOAD_NOTEBOOKS) {
          const projectId = message?.data?.projectId;
          if (!projectId || projectId === this.projectId()) {
            this.loadWorkflows();
          }
        }
      });
    this.subscriptions.add(messageSub);

    // Server-driven workflow-list refresh now arrives via the typed
    // AppEventBus (SCRUM-750). The seam only fires for the watched project,
    // so no project match is needed here.
    this.subscriptions.add(
      this.appEventBus
        .on('reloadWorkflows')
        .subscribe(() => this.loadWorkflows()),
    );
  }

  private loadWorkflows(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }

    const parsedProjectId = parseInt(projectId);
    const versionId = this.appStateService.versionId();

    if (!parsedProjectId || !versionId) {
      return;
    }

    const cachedWorkflows: Workflow[] = this.notebookService.getNotebookList();
    this.updateWorkflowsFromWorkflows(cachedWorkflows);

    const fetchSub = this.notebookService
      .getAllWorkflows(parsedProjectId, versionId)
      .subscribe({
        next: (workflows) => {
          if (workflows && Array.isArray(workflows)) {
            this.updateWorkflowsFromWorkflows(workflows);
          }
        },
        error: () => {
          // Fallback to cached data if API fails - silently handled
        },
      });

    this.subscriptions.add(fetchSub);
  }

  private updateWorkflowsFromWorkflows(workflows: Workflow[]): void {
    const processedWorkflows = workflows
      .filter((workflow) => workflow.workflowId && workflow.name)
      .map((workflow) => ({
        workflowId: workflow.workflowId,
        projectId: parseInt(this.projectId() || '0'),
        versionId: workflow.versionId || this.appStateService.versionId(),
        name: workflow.name,
        defaultState: workflow.defaultState || '',
        headlessOnly: workflow.headlessOnly ?? false,
        description: workflow.description || '',
        createdAt: workflow.createdAt || new Date().toISOString(),
        isActive: workflow.isActive !== undefined ? workflow.isActive : true,
      }));

    this.workflows.set(processedWorkflows);

    if (!this.selectedWorkflowId() && processedWorkflows.length > 0) {
      const selectedNotebook = this.notebookService.getSelectedNotebook();
      if (
        selectedNotebook &&
        selectedNotebook.workflowId &&
        selectedNotebook.name
      ) {
        // Parent component should handle this
      }
    }
  }

  toggleDropdown(): void {
    if (this.disabled()) return;

    if (!this.isOpen()) {
      this.loadWorkflows();
    }

    this.isOpen.update((open) => !open);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  selectWorkflow(workflow: Workflow): void {
    const workflowName = workflow.name;
    this.workflowSelected.emit({
      workflow,
      workflowName,
    });
    this.closeDropdown();
  }

  createNewWorkflow(): void {
    if (!this.canCreateWorkflow()) return;

    const projectId = this.projectId();
    if (!projectId) {
      return;
    }

    const numericProjectId = parseInt(projectId, 10);
    if (isNaN(numericProjectId)) {
      return;
    }

    const dialogData: WorkflowCreateDialogData = {
      projectId: projectId,
      existingWorkflows: this.workflows(),
    };

    const dialogSub = this.dialogService
      .workflowCreateDialog(dialogData)
      .subscribe((result: any) => {
        if (result) {
          if (result.workflow?.workflowId && result.workflow?.name) {
            const newWorkflow: Workflow = {
              workflowId: result.workflow.workflowId,
              projectId: numericProjectId,
              versionId:
                this.appStateService.versionId() ||
                result.workflow?.versionId ||
                1,
              name: result.workflow.name,
              defaultState: '',
              headlessOnly: result.workflow.headlessOnly ?? false,
              description: '',
              createdAt: new Date().toISOString(),
              isActive: true,
            };

            const existingWorkflows = this.workflows();
            const exists = existingWorkflows.some(
              (workflow) => workflow.workflowId === newWorkflow.workflowId,
            );
            if (exists) {
              this.workflows.set(
                existingWorkflows.map((workflow) =>
                  workflow.workflowId === newWorkflow.workflowId
                    ? newWorkflow
                    : workflow,
                ),
              );
            } else {
              this.workflows.set([...existingWorkflows, newWorkflow]);
            }

            // Seed full version cache so the project guard sees the new workflow
            // immediately — a plain cache clear is not enough when the server
            // full-version GET lags behind the create POST (SCRUM-96).
            const versionId = newWorkflow.versionId;
            this.projectService.seedFullVersionCacheWithWorkflow(
              numericProjectId,
              versionId,
              newWorkflow,
              this.appStateService.version() ?? undefined,
            );
            this.notebookService.invalidateWorkflowsCache(
              numericProjectId,
              versionId,
            );

            this.workflowSelected.emit({
              workflow: newWorkflow,
              workflowName: newWorkflow.name,
            });
          }

          this.newWorkflowRequested.emit();
        }
      });

    this.subscriptions.add(dialogSub);
    this.closeDropdown();
  }

  deleteWorkflow(workflow: Workflow, event: Event): void {
    event.stopPropagation();

    // Guard: only proceed when the user actually has permission
    if (!this.canDeleteWorkflow()) return;

    // Fix 5: prevent deleting the last workflow — the project guard requires at least one
    if (this.workflows().length <= 1) {
      this.toastr.error('Cannot delete the last workflow in the project.');
      return;
    }

    const projectId = parseInt(this.projectId() || '0');
    const versionId = this.appStateService.versionId();
    const workflowId = workflow.workflowId;

    if (!projectId || !versionId || !workflowId) return;

    const deleteSub = this.executionPlanService
      .getPlans(projectId)
      .pipe(
        catchError((err) => {
          console.error(
            'WorkflowDropdownComponent: failed to load routines before workflow delete',
            err,
          );
          this.toastr.error(
            'Failed to check connected routines. Please try again.',
          );
          return EMPTY;
        }),
        switchMap((plans) => {
          const connectedRoutines = this.getRoutinesConnectedToWorkflow(
            plans,
            workflow,
          );
          const hasConnectedRoutines = connectedRoutines.length > 0;

          return this.dialogService
            .confirm({
              title: 'Delete Workflow',
              message: hasConnectedRoutines
                ? 'This workflow has connected routines. If you remove the workflow, these routines will also be removed. Continue?'
                : 'This workflow will be removed from the current version. It will remain in any previously published versions. Continue?',
              confirmCaption: 'Delete',
              cancelCaption: 'Cancel',
              isDestructive: true,
            })
            .pipe(
              switchMap((confirmed) => {
                if (!confirmed) {
                  return EMPTY;
                }

                const deleteRoutines$ =
                  connectedRoutines.length > 0
                    ? forkJoin(
                        connectedRoutines.map((routine) =>
                          this.executionPlanService.deletePlan(
                            projectId,
                            routine.routineId,
                          ),
                        ),
                      )
                    : of([] as void[]);

                return deleteRoutines$.pipe(
                  switchMap(() =>
                    this.notebookService.deleteWorkflowWithVersion(
                      projectId,
                      versionId,
                      workflowId,
                    ),
                  ),
                );
              }),
            );
        }),
      )
      .subscribe({
        next: () => {
          // Invalidate cache so the next dropdown open fetches a fresh list
          this.notebookService.invalidateWorkflowsCache(projectId, versionId);
          this.workflows.set(
            this.workflows().filter((w) => w.workflowId !== workflowId),
          );
          this.workflowDeleted.emit(workflowId);
          this.closeDropdown();
        },
        error: (err) => {
          console.error(
            'WorkflowDropdownComponent: failed to delete workflow or connected routines',
            err,
          );
          this.toastr.error('Failed to delete workflow. Please try again.');
        },
      });

    this.subscriptions.add(deleteSub);
  }

  private getRoutinesConnectedToWorkflow(
    plans: ExecPlan[],
    workflow: Workflow,
  ): ExecPlan[] {
    return plans.filter(
      (plan) =>
        plan.workflowId === workflow.workflowId ||
        (!!workflow.name && plan.workflowName === workflow.name),
    );
  }

  formatTimestamp(timestamp: string): string {
    return DateFormattingHelper.formatTimestamp(timestamp);
  }

  isSelectedWorkflow(workflow: Workflow): boolean {
    return workflow.workflowId === this.selectedWorkflowId();
  }

  isActiveWorkflow(workflow: Workflow): boolean {
    const activeId = this.activeWorkflowId();
    return activeId > 0 && workflow.workflowId === activeId;
  }

  getActiveWorkflowName(): string {
    const activeWorkflow = this.activeWorkflow();
    return activeWorkflow?.name || '';
  }

  onDocumentClick(event: Event): void {
    const hostElement = this.elementRef.nativeElement;
    if (!hostElement) {
      this.closeDropdown();
      return;
    }

    const target = event.target as Node | null;
    if (target && target === hostElement) {
      return;
    }

    try {
      if (!hostElement.contains(target as Node)) {
        this.closeDropdown();
      }
    } catch {
      if (target !== hostElement) {
        this.closeDropdown();
      }
    }
  }

  onEscapeKey(): void {
    this.closeDropdown();
  }

  onKeyDown(event: KeyboardEvent, action: string, workflow?: Workflow): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      switch (action) {
        case 'createNewWorkflow':
          this.createNewWorkflow();
          break;
        case 'selectWorkflow':
          if (workflow) {
            this.selectWorkflow(workflow);
          }
          break;
        case 'closeDropdown':
          this.closeDropdown();
          break;
      }
    }
  }
}
